import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TambaLogo from "@/components/TambaLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { 
  ArrowLeft, 
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Briefcase,
  GraduationCap,
  Heart,
  Shield,
  Loader2,
  Check,
  Camera,
  Edit3,
  Save,
  Building2,
  Globe,
  DollarSign,
  Target,
  TrendingUp
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Profile form state
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    gender: "",
    address: {
      street: "",
      city: "",
      state: "",
      zip_code: "",
      country: ""
    },
    profession: "",
    employer: "",
    years_experience: "",
    education: "",
    marital_status: "",
    dependents: "0",
    bio: ""
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchProfile(userData.id);
  }, [navigate]);

  const fetchProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/profile/${userId}`);
      const data = response.data.profile;
      setProfile({
        name: data.name || "",
        email: data.email || "",
        phone: data.phone || "",
        date_of_birth: data.date_of_birth || "",
        gender: data.gender || "",
        address: {
          street: data.address?.street || "",
          city: data.address?.city || "",
          state: data.address?.state || "",
          zip_code: data.address?.zip_code || "",
          country: data.address?.country || ""
        },
        profession: data.profession || "",
        employer: data.employer || "",
        years_experience: data.years_experience || "",
        education: data.education || "",
        marital_status: data.marital_status || "",
        dependents: data.dependents || "0",
        bio: data.bio || ""
      });
    } catch (error) {
      // Use user data if profile endpoint fails
      if (user) {
        setProfile(prev => ({
          ...prev,
          name: user.name || "",
          phone: user.phone || "",
          email: user.email || "",
          profession: user.profile?.profession || "",
          years_experience: user.profile?.years_experience || ""
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/${user.id}`, profile);
      
      // Update local storage
      const updatedUser = { 
        ...user, 
        name: profile.name,
        email: profile.email,
        profile: {
          ...user.profile,
          profession: profile.profession,
          years_experience: profile.years_experience
        }
      };
      localStorage.setItem("tamba_user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      toast.success("Profile updated successfully!");
      setEditMode(false);
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    if (field.startsWith("address.")) {
      const addressField = field.split(".")[1];
      setProfile(prev => ({
        ...prev,
        address: { ...prev.address, [addressField]: value }
      }));
    } else {
      setProfile(prev => ({ ...prev, [field]: value }));
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tamba-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border/30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-heading text-xl font-semibold">My Profile</h1>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {!editMode ? (
              <Button 
                variant="outline"
                onClick={() => setEditMode(true)}
                className="rounded-full"
                data-testid="edit-profile-btn"
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Edit
              </Button>
            ) : (
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-tamba-teal hover:bg-tamba-teal/90"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 pb-24">
        {/* Profile Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="mb-6 overflow-hidden">
            <div className="h-24 bg-gradient-to-r from-tamba-blue via-tamba-teal to-tamba-coral" />
            <CardContent className="relative pt-0 pb-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-end gap-4 -mt-12">
                <div className="relative">
                  <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                    <AvatarFallback className="text-2xl font-bold bg-tamba-blue text-white">
                      {getInitials(profile.name)}
                    </AvatarFallback>
                  </Avatar>
                  {editMode && (
                    <button className="absolute bottom-0 right-0 p-2 bg-tamba-teal rounded-full text-white shadow-lg hover:bg-tamba-teal/90">
                      <Camera className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="text-center sm:text-left flex-1">
                  <h2 className="font-heading text-2xl font-bold">{profile.name || "Your Name"}</h2>
                  <p className="text-muted-foreground">{profile.profession || "Add your profession"}</p>
                  {user?.subscription_status === "premium" && (
                    <span className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-tamba-yellow/20 text-amber-700 dark:text-tamba-yellow rounded-full text-xs font-medium">
                      <Shield className="h-3 w-3" />
                      Premium Member
                    </span>
                  )}
                </div>
                <div className="flex gap-6 text-center">
                  <div>
                    <p className="text-2xl font-bold text-tamba-teal">{user?.risk_tolerance || 50}</p>
                    <p className="text-xs text-muted-foreground">Risk Score</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-tamba-blue">{profile.years_experience || 0}</p>
                    <p className="text-xs text-muted-foreground">Yrs Exp</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Profile Tabs */}
        <Tabs defaultValue="personal" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="personal" data-testid="personal-tab">
              <User className="h-4 w-4 mr-2" />
              Personal
            </TabsTrigger>
            <TabsTrigger value="address" data-testid="address-tab">
              <MapPin className="h-4 w-4 mr-2" />
              Address
            </TabsTrigger>
            <TabsTrigger value="professional" data-testid="professional-tab">
              <Briefcase className="h-4 w-4 mr-2" />
              Professional
            </TabsTrigger>
          </TabsList>

          {/* Personal Info Tab */}
          <TabsContent value="personal">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5 text-tamba-blue" />
                    Personal Information
                  </CardTitle>
                  <CardDescription>Your basic personal details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={profile.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        disabled={!editMode}
                        placeholder="Enter your full name"
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profile.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        disabled={!editMode}
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        value={profile.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        disabled={!editMode}
                        placeholder="+1 (555) 000-0000"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dob">Date of Birth</Label>
                      <Input
                        id="dob"
                        type="date"
                        value={profile.date_of_birth}
                        onChange={(e) => handleChange("date_of_birth", e.target.value)}
                        disabled={!editMode}
                        data-testid="input-dob"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gender">Gender</Label>
                      <Select 
                        value={profile.gender} 
                        onValueChange={(v) => handleChange("gender", v)}
                        disabled={!editMode}
                      >
                        <SelectTrigger data-testid="select-gender">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="non-binary">Non-binary</SelectItem>
                          <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="marital">Marital Status</Label>
                      <Select 
                        value={profile.marital_status} 
                        onValueChange={(v) => handleChange("marital_status", v)}
                        disabled={!editMode}
                      >
                        <SelectTrigger data-testid="select-marital">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single</SelectItem>
                          <SelectItem value="married">Married</SelectItem>
                          <SelectItem value="divorced">Divorced</SelectItem>
                          <SelectItem value="widowed">Widowed</SelectItem>
                          <SelectItem value="domestic-partnership">Domestic Partnership</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dependents">Number of Dependents</Label>
                    <Select 
                      value={profile.dependents} 
                      onValueChange={(v) => handleChange("dependents", v)}
                      disabled={!editMode}
                    >
                      <SelectTrigger className="w-full sm:w-48" data-testid="select-dependents">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 1, 2, 3, 4, 5, "6+"].map((n) => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      value={profile.bio}
                      onChange={(e) => handleChange("bio", e.target.value)}
                      disabled={!editMode}
                      placeholder="Tell us a bit about yourself and your financial goals..."
                      className="min-h-[100px]"
                      data-testid="input-bio"
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Address Tab */}
          <TabsContent value="address">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-tamba-coral" />
                    Address Information
                  </CardTitle>
                  <CardDescription>Your residential address</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="street">Street Address</Label>
                    <Input
                      id="street"
                      value={profile.address.street}
                      onChange={(e) => handleChange("address.street", e.target.value)}
                      disabled={!editMode}
                      placeholder="123 Main Street, Apt 4B"
                      data-testid="input-street"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={profile.address.city}
                        onChange={(e) => handleChange("address.city", e.target.value)}
                        disabled={!editMode}
                        placeholder="New York"
                        data-testid="input-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State / Province</Label>
                      <Input
                        id="state"
                        value={profile.address.state}
                        onChange={(e) => handleChange("address.state", e.target.value)}
                        disabled={!editMode}
                        placeholder="NY"
                        data-testid="input-state"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="zip">ZIP / Postal Code</Label>
                      <Input
                        id="zip"
                        value={profile.address.zip_code}
                        onChange={(e) => handleChange("address.zip_code", e.target.value)}
                        disabled={!editMode}
                        placeholder="10001"
                        data-testid="input-zip"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Select 
                        value={profile.address.country} 
                        onValueChange={(v) => handleChange("address.country", v)}
                        disabled={!editMode}
                      >
                        <SelectTrigger data-testid="select-country">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US">United States</SelectItem>
                          <SelectItem value="CA">Canada</SelectItem>
                          <SelectItem value="UK">United Kingdom</SelectItem>
                          <SelectItem value="AU">Australia</SelectItem>
                          <SelectItem value="DE">Germany</SelectItem>
                          <SelectItem value="FR">France</SelectItem>
                          <SelectItem value="IN">India</SelectItem>
                          <SelectItem value="JP">Japan</SelectItem>
                          <SelectItem value="BR">Brazil</SelectItem>
                          <SelectItem value="MX">Mexico</SelectItem>
                          <SelectItem value="NG">Nigeria</SelectItem>
                          <SelectItem value="ZA">South Africa</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Professional Tab */}
          <TabsContent value="professional">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-tamba-teal" />
                    Professional Information
                  </CardTitle>
                  <CardDescription>Your career and education details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="profession">Profession / Job Title</Label>
                      <Input
                        id="profession"
                        value={profile.profession}
                        onChange={(e) => handleChange("profession", e.target.value)}
                        disabled={!editMode}
                        placeholder="Software Engineer"
                        data-testid="input-profession"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="employer">Current Employer</Label>
                      <Input
                        id="employer"
                        value={profile.employer}
                        onChange={(e) => handleChange("employer", e.target.value)}
                        disabled={!editMode}
                        placeholder="Acme Corporation"
                        data-testid="input-employer"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="experience">Years of Experience</Label>
                      <Select 
                        value={profile.years_experience} 
                        onValueChange={(v) => handleChange("years_experience", v)}
                        disabled={!editMode}
                      >
                        <SelectTrigger data-testid="select-experience">
                          <SelectValue placeholder="Select years" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-2">0-2 years</SelectItem>
                          <SelectItem value="3-5">3-5 years</SelectItem>
                          <SelectItem value="6-10">6-10 years</SelectItem>
                          <SelectItem value="11-15">11-15 years</SelectItem>
                          <SelectItem value="16-20">16-20 years</SelectItem>
                          <SelectItem value="20+">20+ years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="education">Highest Education</Label>
                      <Select 
                        value={profile.education} 
                        onValueChange={(v) => handleChange("education", v)}
                        disabled={!editMode}
                      >
                        <SelectTrigger data-testid="select-education">
                          <SelectValue placeholder="Select education" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high-school">High School</SelectItem>
                          <SelectItem value="associate">Associate Degree</SelectItem>
                          <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                          <SelectItem value="master">Master's Degree</SelectItem>
                          <SelectItem value="doctorate">Doctorate / PhD</SelectItem>
                          <SelectItem value="professional">Professional Degree (MD, JD, etc.)</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="font-medium mb-4 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      Financial Profile Summary
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="p-3 bg-secondary/50 rounded-xl text-center">
                        <p className="text-xs text-muted-foreground mb-1">Income Range</p>
                        <p className="font-semibold text-sm">{user?.income_range || "Not set"}</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-xl text-center">
                        <p className="text-xs text-muted-foreground mb-1">Savings Range</p>
                        <p className="font-semibold text-sm">{user?.current_savings || "Not set"}</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-xl text-center">
                        <p className="text-xs text-muted-foreground mb-1">Risk Tolerance</p>
                        <p className="font-semibold text-sm">{user?.risk_tolerance || 50}/100</p>
                      </div>
                      <div className="p-3 bg-secondary/50 rounded-xl text-center">
                        <p className="text-xs text-muted-foreground mb-1">Accredited</p>
                        <p className="font-semibold text-sm">{user?.is_accredited_investor ? "Yes" : "No"}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ProfilePage;
